<template>
  <app-loader
    id="AccountLogin"
    :action="loadUsers"
    class="d-flex flex-column ga-4 align-center"
  >
    <div v-if="!small || selectedUserId == $auth.user!.id">
      <h2
        class="text-center mb-8"
        v-if="!small"
      >
        <template v-if="$route.query['after-login']">
          Let’s log you in. Just choose your profile to get started.
        </template>
        <template v-else>
          Welcome Back
        </template>
      </h2>
      <UserSelector
        v-model="selectedUserId"
        :users="users"
        :disabled="!!loading || otpLogin"
        :scroll="small"
        :show-impersonate="showImpersonate"
        @click="scrollToBottom"
      />
    </div>

    <template v-if="!small || selectedUserId != $auth.user!.id">
      <div
        v-if="otpLogin"
        class="text-center"
      >
        <span class="text-secondary">We sent a code to mobile number: </span>
        <b>{{ formatPhone(selectedUser?.phone) }}</b>
      </div>

      <div
        class="text-center"
        v-if="selectedUser?.has_pin"
      >
        <h6 v-if="!small">
          {{ otpLogin ? 'Enter your one-time passcode' : 'Please enter your pin' }}.
        </h6>

        <v-otp-input
          base-color="primary"
          v-model="pin"
          :error="!!error"
          :length="otpLogin ? 6 : 4"
          width="250"
          @change="error = ''"
          @keydown.enter="submit()"
          @finish="small && submit()"
        />
        <div
          class="text-red text-center"
          v-if="error"
          v-text="error"
        />

        <div
          v-if="!otpLogin && !small"
          class="text-center mt-2"
        >
          <v-checkbox
            v-model="rememberDevice"
            class="d-inline-block"
            density="compact"
            hide-details
            label="Remember this device"
          />
        </div>

        <div
          v-if="!otpLogin && selectedUser?.phone"
          class="text-link"
          @click="startOtpLogin"
        >
          Forgot your pin?
        </div>
        <div
          v-if="otpLogin"
          class="text-center mb-4"
        >
          <div v-if="resendSeconds == 0">
            <span class="text-secondary">Didn’t get a code?</span> <span
              class="text-link"
              @click="resend"
            >Click to resend</span>
          </div>
          <div
            v-else
            class="text-secondary"
          >
            Resent! Can resend again in {{ resendSeconds }}
          </div>
        </div>

        <v-btn-primary
          class="mt-4 px-14"
          :loading="loading === 'login'"
          @click="submit()"
        >
          Submit
        </v-btn-primary>

        <div
          v-if="!small && otpLogin"
        >
          <v-btn-secondary
            class="mt-4 px-16 mt-4"
            @click="otpLogin = false; pin = ''"
          >
            Back
          </v-btn-secondary>
        </div>
      </div>
      <div
        v-else
        class="text-center"
      >
        <div class="text-secondary mb-16">
          {{ small ? 'Logging in...' : 'No pin required.' }}
        </div>

        <v-btn-primary
          class="px-16"
          :loading="loading === 'login'"
          @click="submit(true)"
        >
          Proceed
        </v-btn-primary>
      </div>
    </template>

    <pin-dialog
      v-model="dialogs['resetPin']"
      :key="dialogKeys['resetPin']"
      :user="$auth.user!"
      reset
      @close="navigateToCalendar"
    />
  </app-loader>
</template>
<script lang="ts">
import AppLoader from "@/components/AppLoader.vue"
import UserSelector from "@/components/UserSelector.vue"
import validators from "@/mixins/validators"
import format from "@/mixins/format"
import dialog from "@/mixins/dialog"
import PinDialog from "@/components/PinDialog.vue"
import type {PropType} from "vue"
import {Device} from "@capacitor/device"

export default {
  name: 'AccountLogin',
  components: {PinDialog, AppLoader, UserSelector},
  props: {
    modelValue: {
      type: Object as PropType<App.Models.User | null>,
      default: null
    },
    small: Boolean,
    showImpersonate: Boolean,
  },
  mixins: [validators, format, dialog],
  emits: ['update:modelValue', 'success'],
  data() {
    return {
      loading: '',
      error: '',
      users: [] as App.Models.User[],
      selectedUserId: null as number | null,
      pin: '',
      otpLogin: false,
      resendSeconds: 0,
      resendTimer: null as any,
      userIdPreSelected: false,
      rememberDevice: false,
      trustVerified: false,
    }
  },
  mounted() {
    this.selectedUserId = this.$auth.user!.id

    this.resendTimer = setInterval(() => {
      if (this.resendSeconds > 0) {
        this.resendSeconds--
      }
    }, 1000)

    const userId = parseInt(this.$route.query['id']) || null
    if (userId) {
      this.userIdPreSelected = userId
      const currentUrl = window.location.href
      const newUrl = currentUrl.split('?')[0]
      history.replaceState({}, '', newUrl)
    }
  },
  computed: {
    selectedUser() {
      return this.users.find(u => u.id == this.selectedUserId)
    }
  },
  watch: {
    modelValue(user: App.Models.User | null) {
      if (user) {
        this.selectedUserId = user.id
      }
    },
    selectedUserId(val) {
      if (val !== this.modelValue?.id) {
        this.$emit('update:modelValue', this.selectedUser)

        if (this.small && !this.selectedUser.has_pin) {
          this.submit(true)
        }
      }
    }
  },
  methods: {
    async loadUsers() {
      // Try the trust token first so the AppLoader spinner covers the redirect.
      // On success we skip the /users round-trip entirely. On failure we fall through
      // to the normal selector flow.
      if (await this.tryPinTrustSkip()) {
        // Hold the loader open until the route push unmounts us — otherwise AppLoader
        // would briefly render the selector slot between resolve and navigation.
        return new Promise<void>(() => { /* never resolves */ })
      }

      this.loading = 'users'
      const {data: {data}} = await this.$http.get(`/users`, {
        params: {
          itemsPerPage: -1
        }
      }).catch(e => e)
      this.loading = ''

      this.users = data

      // Single-user accounts with no PIN have nothing to choose and nothing to gate —
      // skip the "No pin required. Proceed" screen and go straight to the calendar.
      // Same hold-the-loader-open trick as tryPinTrustSkip so we never flash the slot.
      // Skipped in `small` mode (in-app Switch Profile) — that surface shouldn't bounce.
      if (!this.small && data?.length === 1 && !data[0].has_pin) {
        this.navigateToCalendar()
        return new Promise<void>(() => { /* never resolves */ })
      }

      if (this.userIdPreSelected) {
        this.selectedUserId = this.userIdPreSelected
        this.userIdPreSelected = false
      }
    },

    /**
     * Returns true if a stored trust token verified successfully and we navigated to the calendar.
     *
     * The token's signed `sub` claim is the source of truth — we don't pass a target user
     * id. The backend resolves the trust to its user, swaps the JWT, and we navigate to that
     * user's calendar. This way an AO who ticked Remember on profile A and then briefly
     * switched to B still lands back on A on the next launch.
     *
     * Skipped in `small` mode (the in-app "Switch Profile" UI) — the user's intent there is
     * explicitly to pick a different profile, so silently bouncing them back to the calendar
     * via the trust token is wrong.
     */
    async tryPinTrustSkip(): Promise<boolean> {
      if (this.trustVerified) return false
      this.trustVerified = true

      if (this.small) return false

      const { valid } = await this.$auth.verifyPinTrust()
      if (!valid) return false

      // Don't unmount until after navigation kicks in; AppLoader keeps its spinner visible.
      this.navigateToCalendar()
      return true
    },

    /**
     * When selecting a user, scroll to the bottom of the account login card
     */
    scrollToBottom() {
      this.$nextTick(() => {
        const el = document.querySelector('#AccountLogin').parentElement
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
      })
    },

    startOtpLogin() {
      this.otpLogin = true
      // OTP recovery invalidates the device's trust on the client; backend
      // resigns trust when a new PIN is set, so we don't want a stale token sitting around.
      this.$auth.clearPinTrust()
      this.sendOtp()
    },
    async sendOtp() {
      this.error = ''
      const { data: { code, message, errors }, status } = await this.$auth.sendOtp({phone: this.selectedUser?.phone, register: false, reset: true}).catch(e => e)

      if (this.$error(status, message, errors, false)) {
        this.error = message
        return
      }

      if (code) {
        this.pin = code
      }
    },
    async submit(noPin = false) {
      if (this.otpLogin) {
        await this.login()
      } else {
        await this.pinLogin(noPin)
      }
    },
    async pinLogin(noPin = false) {
      if (!this.selectedUserId || this.loading) return

      this.error = ''
      this.loading = 'login'
      const deviceId = (await Device.getId().catch(() => null))?.identifier
      const { data: { message, errors }, status } = await this.$auth.pinLogin({
        user_id: this.selectedUserId,
        pin: noPin ? '' : this.pin,
        // Only meaningful when a PIN was actually entered — never establish trust on a no-pin path.
        // Also suppressed in `small` mode (in-app profile switcher): the checkbox is hidden there,
        // but the belt-and-suspenders check guarantees no trust slot can be set from a profile switch.
        remember: !noPin && !this.small && this.rememberDevice,
        device_id: deviceId,
      }).catch(e => e)
      this.loading = ''

      if (this.$error(status, message, errors, false)) {
        this.error = message
        return
      }

      this.navigateToCalendar()
    },
    async login() {
      if (this.loading) return

      this.error = ''
      this.loading = 'login'
      const { data: { access_token, message, errors }, status } = await this.$http.post('/auth/verify-pin-reset-otp', {
        otp: this.pin,
        phone: this.selectedUser?.phone,
      }).catch(e => e)
      this.loading = ''

      if (this.$error(status, message, errors, false)) {
        this.error = message
        return
      }

      if (access_token) {
        localStorage.setItem('token', access_token)
        await this.$auth.loadUser(true)
      }

      // before navigating, show reset pin modal
      this.openDialog('resetPin')
    },
    async resend() {
      this.error = ''
      const { data: { message, errors }, status } = await this.$auth.sendOtp({phone: this.selectedUser?.phone, register: false}).catch(e => e)
      if (this.$error(status, message, errors, false)) {
        this.error = message
        return
      }

      this.resendSeconds = 30
    },
    navigateToCalendar() {
      this.$emit('success')
      this.$router.push(this.$routeTo(this.ROUTES.CALENDAR))
      this.pin = ''
    }
  }
}
</script>
<style lang="scss" scoped>

.account-select-wrapper {
  height: 600px;
  max-height: 600px;
}
</style>
